import template from './loginView.html';
import Logger from '../../shared/logger';
import { Async, Inject, withAngularComponent } from '../base/decorators';
import BaseComponent from '../base/component';
import './loginStyles.scss';
import { authErrorCodes, authTypes } from '../core/authentication/authenticationService';

@withAngularComponent({
    name: 'loginComponent',
    template,
    controllerAs: 'loginCtrl',
})
export class LoginComponent extends BaseComponent {
    @Inject('$scope') $scope;
    @Inject('$state') $state;
    @Inject('$stateParams') $stateParams;
    @Inject('$translate') $translate;
    @Inject('$localStorage') $localStorage;
    @Inject('$sessionStorage') $sessionStorage;
    @Inject('$timeout') $timeout;
    @Inject('$filter') $filter;
    @Inject('FE_VERSION') FE_VERSION;
    @Inject('AuthService') authService;
    @Inject('NotificationService') notificationService;
    @Inject('RouteService') routeService;
    @Inject('BrowserService') browserService;
    @Inject('ModalDialogService') modalDialogService;

    async setupComponentData() {
        this.error = false;
        this.showEmployerRepRoute = this.$stateParams.isEmployerRep;
        this.employerRepId = this.$stateParams.employerRepId;
        this.$localStorage.version = this.FE_VERSION.version;
        this.forgotPasswordErrorMessage = '';
        this.$forgetPasswordModal = $('#modal-forgot-password');
        this.isLoginWithLinkedInAvailable = await this.routeService.getSystemDefaultsAsync('isLoginWithLinkedInAvailable');
        this.tftMainLogoUrl = await this.routeService.getThemeSettingsAsync('tftMainLogoUrl') || '';

        this.userBlockedDialog = {
            show: false,
            message: '',
        };

        if (!this.browserService.isChrome) {
            this.showBrowserDisclaimer();
        }
    }

    showBrowserDisclaimer() {
        this.$scope.$on('TRANSLATIONS_LOADED', () => {
            const translatedLabel = this.$filter('translate')('BROWSERS_DISCLAIMER_HEADER');

            this.modalDialogService.showDialog({
                modalDialogId: 'browsersDisclaimerModalDialog',
                body: `
                    <img class="browser-disclaimer__logo" src="/assets/img/CHROME.gif"/>
                    <span class="browser-disclaimer__title">${translatedLabel}</span>
                `,
                hideHeader: true,
                showCancelButton: false,
                showCloseButton: false,
                onClose: () => {
                },
                buttons: [{
                    label: 'BROWSERS_DISCLAIMER_BUTTON',
                    className: 'default-button',
                    onClick: () => window.open('https://www.google.com/chrome/'),
                }],
            });
        });
    }

    backToLogin(){
        this.userBlockedDialog.show = false;
        this.userBlockedDialog.message = '';
    }

    @Async()
    async continue(userType) {
        this.$sessionStorage.userType = userType;

        if (!this.username || !this.password) {
            this.submitted = true;
            return;
        }
        this.error = false;

        try {
            const authType = await this.getUserAuthType(this.username);
            if (authType === authTypes.LINKEDIN) {
                this.notificationService.error('LOGIN_LINKEDIN_PASSWORD_CHANGE_ERROR_MESSAGE');
                return;
            }
            const { error, errorCode } = await this.authService.login({
                username: this.username,
                password: this.password,
            });

            if (error) {
                if (errorCode === authErrorCodes.TOO_MANY_ATTEMPTS) {
                    this.userBlockedDialog.show = true;
                    this.userBlockedDialog.message = error;
                } else {
                    this.error = true;
                    this.notificationService.error(error);
                }
            }
        } catch (error) {
            Logger.error(error);
        }
    }

    loginWith(loginWithApp, userType) {
        this.$sessionStorage.userType = userType;
        this.authService.loginWith(loginWithApp);
    }

    validateEmail(email) {
        // eslint-disable-next-line no-useless-escape
        const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

        return re.test(email);
    }

    async isUserExists(email) {
        return this.routeService.isUserExists({ userId: email });
    }

    async getUserAuthType(email) {
        return this.routeService.getUserAuthType({ userId: email });
    }

    async isUserActive(email) {
        return this.routeService.isUserActive({ userId: email });
    }

    signUpEmployerRep(employerRepId = null, isCreateNewEmplRepAction = false) {
        this.$localStorage.employerRepReturnUrlParams = { targetEmployerRepId: employerRepId };

        return this.$state.go('employerRep.signUp.details', {
            employerRepId,
            isCreateNewEmplRepAction,
        });
    }

    forgotPasswordShowClick() {
        this.forgotPasswordErrorMessage = '';
        this.forgotPasswordEmail = this.username;
    }

    @Async()
    async forgotPasswordResetClick() {
        this.error = false;
        this.forgotPasswordErrorMessage = '';
        if (!this.forgotPasswordEmail) {
            this.forgotPasswordErrorMessage = 'FORGOT_PASSWORD_EMAIL_EMPTY_MESSAGE';
            return;
        } else if (!this.validateEmail(this.forgotPasswordEmail)) {
            this.forgotPasswordErrorMessage = 'FORGOT_PASSWORD_EMAIL_INVALID_MESSAGE';
            return;
        }

        const isUserExists = await this.isUserExists(this.forgotPasswordEmail);
        if (!isUserExists) {
            this.notificationService.success('FORGOT_PASSWORD_RESPONSE_MESSAGE');
            this.$forgetPasswordModal.modal('hide');
            return;
        }

        const isActive = await this.isUserActive(this.forgotPasswordEmail);
        if (!isActive) {
            this.notificationService.error('LOGIN_USER_IS_INCATIVE_ERROR_MESSAGE');
            this.$forgetPasswordModal.modal('hide');
            return;
        }

        const authType = await this.getUserAuthType(this.forgotPasswordEmail);
        if (authType === authTypes.LINKEDIN) {
            this.notificationService.error('LOGIN_LINKEDIN_PASSWORD_CHANGE_ERROR_MESSAGE');
            this.$forgetPasswordModal.modal('hide');
            return;
        }

        try {
            await this.authService.changePassword(this.forgotPasswordEmail);
            await this.routeService.blockUserSessions({ email: this.forgotPasswordEmail });
            this.notificationService.success('FORGOT_PASSWORD_RESPONSE_MESSAGE');
            this.$forgetPasswordModal.modal('hide');
            this.routeService.sendPasswordResetRequestNotification({ email: this.forgotPasswordEmail });
        } catch (err) {
            Logger.error(err);
        }
    }
}
